import React from "react";
import { Box, Chip, styled } from "@mui/material";
import { ItemType } from "../../types/types";
import ItemGallery, { hasItemImages } from "./ItemGallery";
import Price from "./Price";
import Name from "./Name";
import ItemInfo from "./ItemInfo";
import ItemRelated from "./ItemRelated";
import PurchaseActions from "./PurchaseActions";
import DeliveryEstimate from "../Delivery/DeliveryEstimate";
import Typography from "@mui/material/Typography";
import ShareButton from "../ShareButton/ShareButton";
import StarRating from "../StarRating/StarRating";
import FavoriteButton from "../FavoriteButton/FavoriteButton";
import { StyledDivider } from "./styledComponents";
import { CATEGORY_LABELS } from "../../pages/shared/constants";

/**
 * Composition only (EB-541).
 *
 * This file decides the grid and the order of the regions; the regions themselves live in
 * `ItemGallery`, `ItemRelated` and `PurchaseActions`. The split exists so the redesign tickets can
 * run in parallel — before it, three of them rewrote this one file, and each rewrote it twice,
 * because both the desktop and the mobile variant live here. See
 * `docs/product-detail-redesign/README.md` §7.
 */

interface ItemGridProps {
  $hasImages: boolean;
}
const ItemGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$hasImages",
})<ItemGridProps>(({ theme, $hasImages }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr",
  width: "100%",
  ...($hasImages && {
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },
  }),
}));

// Stacks since EB-539: the product grid, then the full-width related-products band beneath it.
const ItemContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.spacing(1),
}));

const PriceRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}));

const MobilePriceRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}));

const MobilePriceValue = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "100%",
  justifyContent: "flex-end",
  marginTop: theme.spacing(1),
}));

type ItemProps = {
  item: ItemType;
  // The quantity comes from the selector inside `PurchaseActions` (EB-538); this file only passes
  // the callbacks through. Callers with no selector can still supply a zero-argument function.
  onAddToCart: (quantity: number) => void;
  handlePurchase: (quantity: number) => void;
};

export const Item: React.FC<ItemProps> = ({ item, onAddToCart, handlePurchase }) => {
  const hasImages = hasItemImages(item);

  return (
    <ItemContainer>
      <ItemGrid py={1} $hasImages={hasImages}>
        <ItemGallery item={item} variant="desktop" />
        <Box p={1}>
          <Box px={1}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Name item={item} />
              <Box display="flex" alignItems="center">
                {item.id && <FavoriteButton itemId={item.id} size="medium" />}
                <ShareButton name={item.name} productId={item.id} size="medium" />
              </Box>
            </Box>
            <StarRating value={item.rating} size="medium" />
            {item.tipo_producto && (
              <Chip
                label={CATEGORY_LABELS[item.tipo_producto] ?? item.tipo_producto}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            )}
            {/* No longer gated on a price: `Price` renders "Precio a cotizar" for the 107 products
                that have none, and hiding the row entirely was what made those pages look broken. */}
            <StyledDivider sx={{ my: 1 }} />
            <PriceRow>
              <Typography variant="h6" fontWeight="bold">
                Precio:
              </Typography>
              <Price item={item} />
            </PriceRow>
            <PurchaseActions
              variant="desktop"
              item={item}
              onAddToCart={onAddToCart}
              handlePurchase={handlePurchase}
            />
            {/* Directly under the buy block, not further down the page: the point is that the fee
                is seen while deciding, not after committing (EB-548, README §5.5). */}
            <DeliveryEstimate item={item} />
          </Box>
        </Box>
      </ItemGrid>
      {/* Below the grid, full width (EB-557). Everything above is the decision to buy and belongs
          beside the photos; the description and the specs are reading material and were being read
          through a half-width slot with an empty page beside it. */}
      <ItemInfo item={item} />
      <ItemRelated item={item} />
    </ItemContainer>
  );
};

export const ItemMobile: React.FC<ItemProps> = ({ item, onAddToCart, handlePurchase }) => {
  const hasImages = hasItemImages(item);

  return (
    <ItemContainer>
      <ItemGrid p={0} $hasImages={hasImages}>
        <Box p={2}>
          <Box p={1} display="flex" justifyContent="space-between" alignItems="flex-start">
            <Name item={item} />
            <Box display="flex" alignItems="center">
              {item.id && <FavoriteButton itemId={item.id} size="medium" />}
              <ShareButton name={item.name} productId={item.id} size="medium" />
            </Box>
          </Box>
          <Box px={1}>
            <StarRating value={item.rating} size="medium" />
            {item.tipo_producto && (
              <Chip
                label={CATEGORY_LABELS[item.tipo_producto] ?? item.tipo_producto}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            )}
          </Box>
          <StyledDivider sx={{ mt: 1 }} />
          <MobilePriceRow>
            <Typography variant="h6" fontWeight="bold">
              Precio:
            </Typography>
            <MobilePriceValue>
              <Price item={item} />
            </MobilePriceValue>
          </MobilePriceRow>
          <StyledDivider sx={{ mt: 1 }} />
        </Box>
        <ItemGallery item={item} variant="mobile" />
        <Box p={1}>
          <StyledDivider sx={{ mt: 1 }} />
          <PurchaseActions
            variant="mobile"
            item={item}
            onAddToCart={onAddToCart}
            handlePurchase={handlePurchase}
          />
          <DeliveryEstimate item={item} />
        </Box>
      </ItemGrid>
      {/* The same band as desktop. Mobile was already one column, so this changes nothing about
          the order or the look — it just stops the two variants describing it separately. */}
      <ItemInfo item={item} />
      <ItemRelated item={item} />
    </ItemContainer>
  );
};
